import React, { useState, useEffect } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { ff } from '../../../theme';

interface ContentDueCountdownProps {
  dueDate: Date;
  subtitle?: string;
  brandName?: string;
  /** Heading ("Draft due" / "Post by" / "Post on" / "Post between"). Omitted = no heading. */
  label?: string;
  /** The deadline date under the heading, e.g. "Oct 23" / "Oct 26 – Nov 1". Omitted = none. */
  dateText?: string;
  /** Second line while a go-live draft is owed, e.g. "Then post on Oct 23". Omitted = none. */
  postText?: string;
}

const calcTimeLeft = (dueDate: Date) => {
  const diff = dueDate.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, mins: 0, overdue: true };
  const totalMins = Math.floor(diff / 60000);
  const days = Math.floor(totalMins / (60 * 24));
  const hours = Math.floor((totalMins % (60 * 24)) / 60);
  const mins = totalMins % 60;
  return { days, hours, mins, overdue: false };
};

const ContentDueCountdown: React.FC<ContentDueCountdownProps> = ({ dueDate, brandName, subtitle: subtitleProp, label, dateText, postText }) => {
  const [timeLeft, setTimeLeft] = useState(() => calcTimeLeft(dueDate));

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calcTimeLeft(dueDate));
    }, 60000);
    return () => clearInterval(interval);
  }, [dueDate]);

  // Board: "4D 6H", or "6H 12M" inside the last day.
  const big = timeLeft.overdue
    ? 'Overdue'
    : timeLeft.days > 0
    ? `${timeLeft.days}D ${timeLeft.hours}H`
    : `${timeLeft.hours}H ${timeLeft.mins}M`;
  const subtitle = subtitleProp ?? 'Submit by deadline to receive compensation';

  return (
    <Flex
      as="section"
      aria-label={timeLeft.overdue ? 'Content overdue' : 'Content due'}
      p="16px 18px"
      borderRadius="20px"
      bg="#0E0E0E"
      color="#FFFFFF"
      align="flex-end"
      justify="space-between"
      gap="12px"
      fontFamily="display"
      onClick={(e) => e.stopPropagation()}
    >
      <Flex direction="column" gap="6px" minW={0}>
        <Text fontSize="11px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color="#B4B4B8" lineHeight="normal">
          {label ?? 'Content due in'}
        </Text>
        <Text textStyle="display" fontSize="36px" color={timeLeft.overdue ? ff.danger : ff.lime} whiteSpace="nowrap">
          {big}
        </Text>
      </Flex>
      <Flex direction="column" align="flex-end" gap="2px" textAlign="right" fontSize="13px" lineHeight={1.4} color="#B4B4B8" minW={0}>
        {dateText && <Text as="strong" color="#FFFFFF" fontSize="15px" lineHeight="normal">{dateText}</Text>}
        {postText && <Text>{postText}</Text>}
        <Text fontSize="12px">{subtitle}</Text>
        {brandName && <Text fontSize="12px" fontWeight={600} color="#C6F432" noOfLines={1}>Set by {brandName}</Text>}
      </Flex>
    </Flex>
  );
};

export default ContentDueCountdown;
